/**
 * card_swipe_controller.js — swipe gestures on the mobile entity-list cards (Stimulus).
 *
 * Attached to a list's `.table-responsive` (data-controller="card-swipe"). On mobile
 * (<=767px) each `<tbody> <tr>` renders as a card; this controller lets each card be
 * dragged horizontally to reveal an action pane and fire it on release:
 *
 *   - swipe RIGHT  -> run the status workflow's next forward action — the form already
 *                     in the row tagged `data-card-primary-action` (Accept / Mark Paid /
 *                     Start / Complete / ...). The form is submitted via requestSubmit()
 *                     so the existing CSRF, data-confirm-email modal, and Turbo Stream
 *                     row replacement all run unchanged. A pane is revealed under the
 *                     sliding card; release past max(72px, 40% of the row) fires, a
 *                     shorter drag snaps back. Terminal statuses (no forward action)
 *                     don't arm.
 *
 *   - swipe LEFT   -> open the card's detail page via an interactive, finger-scrubbed
 *                     page transition. Two phases: (1) the card slides with the finger
 *                     (View pane revealed) up to the "hook" — the distance needed to show
 *                     the stacked icon + "View" label, measured from the pane; (2) at the
 *                     hook the detail page starts loading, a full-viewport incoming layer
 *                     is built (the detail skeleton instantly, real content swapped in
 *                     when the fetch returns), and the gesture switches to page-slide: the
 *                     outgoing page and the incoming detail layer both move 1:1 with the
 *                     finger and with each other (no parallax). Release past COMMIT_FRACTION
 *                     of the screen commits the navigation (Turbo.visit, body swapped under
 *                     the overlay); a shorter drag scrubs everything back, no navigation.
 *
 * Pointer Events cover touch + mouse; axis detection on the first move lets vertical
 * gestures fall through to native scroll and lets a plain tap reach the card-expand
 * handler (mobile-cards.js). A horizontal drag swallows its trailing click so it never
 * also toggles the card.
 *
 * Lifecycle: connect() binds pointerdown on the element and move/up on window;
 * disconnect() removes them and clears any in-flight pane/overlay/transform.
 */

import { Controller } from "@hotwired/stimulus";
import { bump, crossedThreshold, flipSettle, lockAxis, motionToken, rubberBand } from "../../gestures.js";

var MIN_FIRE_PX = 72;        // absolute floor before the RIGHT-swipe action fires
var FIRE_FRACTION = 0.4;     // ...or this fraction of the row width
var AXIS_DEADZONE = 8;       // px before committing to a horizontal/vertical axis
var MOBILE_MAX = 767;        // matches the table->card CSS breakpoint

// LEFT-swipe (view) interactive transition tuning.
var HOOK_FRACTION = 0.25;    // card travel (fraction of row width) that triggers the hook
var COMMIT_FRACTION = 0.28;  // post-hook progress (0..1 of full screen) needed to navigate; the settle animation covers the rest

// RIGHT-swipe (action) completion. The card flings the rest of the way off-screen,
// then the result continues: rows slide up to close the gap (remove) or the updated
// row slides back in (replace). Distinct from turbo-animations.js's generic transitions.
var ACTION_FLING_MS = 240;   // card continues off-screen from the release point
var ACTION_SETTLE_MS = 260;  // gap-close slide-up / replacement slide-in

export default class extends Controller {
    connect() {
        this.active = null;
        this.startX = 0;
        this.startY = 0;
        this.axis = null;     // 'h' | 'v' | null (undecided)
        this.dir = 0;         // -1 left, +1 right
        this.armed = false;   // past the fire threshold this move
        this.pane = null;     // revealed action pane for the active drag
        this.viewHookPx = 0;  // measured hook distance for the view pane (icon + label)
        // The active row's affordances, resolved once at _down (they don't change mid-drag).
        this.primaryForm = null;  // the tagged forward-action form, or null
        this.viewUrl = null;      // the detail href, or null

        // LEFT-swipe interactive transition state.
        this.hooked = false;  // past the hook -> page-slide phase owns the gesture
        this.hookDx = 0;      // finger dx at the moment of the hook
        this.viewW = 0;       // viewport width captured at the hook
        this.progress = 0;    // 0..1 post-hook slide progress
        this.overlay = null;  // incoming detail layer (lives on <html>, survives the swap)
        this.overlayPage = null;
        this.committing = false;  // true while a committed transition hands off to Turbo

        this._onDown = this._down.bind(this);
        this._onMove = this._move.bind(this);
        this._onUp = this._up.bind(this);

        this.element.addEventListener('pointerdown', this._onDown);
        window.addEventListener('pointermove', this._onMove, { passive: false });
        window.addEventListener('pointerup', this._onUp);
        window.addEventListener('pointercancel', this._onUp);
    }

    disconnect() {
        this.element.removeEventListener('pointerdown', this._onDown);
        window.removeEventListener('pointermove', this._onMove);
        window.removeEventListener('pointerup', this._onUp);
        window.removeEventListener('pointercancel', this._onUp);
        this._removePane();
        if (this._streamHandler) document.removeEventListener('turbo:before-stream-render', this._streamHandler);
        if (this._firingId) this._endFire(this._firingId);
        if (this.active) this._resetRow(this.active);
        // Only tear the overlay down here if we're NOT committing — a committing
        // overlay must outlive this disconnect (Turbo swaps <body>, disconnecting the
        // controller) so it can cover the navigation; its own turbo:load handler
        // removes it. A non-committing leftover (rare) gets cleaned up.
        if (this.overlay && !this.committing) this._teardownOverlay();
        if (this._swipeFetchController) { this._swipeFetchController.abort(); this._swipeFetchController = null; }
        if (this._fireTimer) { clearTimeout(this._fireTimer); this._fireTimer = null; }
        this.active = null;
    }

    _down(e) {
        if (window.innerWidth > MOBILE_MAX) return;       // desktop keeps the real table
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        var row = e.target.closest('tbody tr');
        if (!row || !this.element.contains(row)) return;
        if (row.classList.contains('empty-state-row')) return;
        // Let taps on the card's own controls (action buttons, links, inputs) behave
        // natively — only blank-card drags are swipes. EXCEPTION: the "eye" view link is
        // also a valid drag handle. Starting there still tracks the gesture: a pure tap
        // navigates natively (axis never goes horizontal, no click swallow), a horizontal
        // drag becomes a swipe (and _up swallows the pending click).
        var ctrl = e.target.closest('a, button, input, textarea, select, label, form');
        if (ctrl && !ctrl.closest('.mobile-card-eye, a.btn-view')) return;
        // Resolve the row's affordances once — they don't change during the drag, so
        // there's no need to re-query the DOM on every move. Need at least one to proceed.
        var primaryForm = this._primaryForm(row);
        var viewUrl = this._viewUrl(row);
        if (!primaryForm && !viewUrl) return;

        this.active = row;
        this.primaryForm = primaryForm;
        this.viewUrl = viewUrl;
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.axis = null;
        this.dir = 0;
        this.armed = false;
        this.hooked = false;
        this.progress = 0;
        this.viewHookPx = 0;
        // Capture the row's UNTRANSFORMED box now (no transform applied yet) so the
        // revealed pane can be pinned to where the card sits, not where it slides to.
        // getBoundingClientRect during a drag would include translateX and glue the
        // pane under the moving card (invisible).
        var cRect = this.element.getBoundingClientRect();
        var rRect = row.getBoundingClientRect();
        this.baseBox = {
            top: rRect.top - cRect.top,
            left: rRect.left - cRect.left,
            width: rRect.width,
            height: rRect.height
        };
    }

    _move(e) {
        if (!this.active) return;

        // Once hooked, the LEFT-swipe page-slide owns every move (card is frozen).
        if (this.hooked) { this._trackPage(e); return; }

        var dx = e.clientX - this.startX;
        var dy = e.clientY - this.startY;

        if (this.axis === null) {
            var axis = lockAxis(dx, dy, AXIS_DEADZONE);
            if (axis === null) return;                    // still inside the deadzone
            this.axis = axis;
            if (this.axis === 'h') this.active.classList.add('is-swiping');
        }
        if (this.axis !== 'h') return;                    // vertical -> native scroll

        e.preventDefault();
        var row = this.active;
        var width = row.offsetWidth || 1;
        this.dir = dx < 0 ? -1 : 1;

        // Is this direction available? Left needs a view; right needs a forward action.
        var available = this.dir < 0 ? !!this.viewUrl : !!this.primaryForm;
        if (!available) {
            // Rubber-band so the card resists but nothing arms or reveals.
            this._removePane();
            this.armed = false;
            var damped = rubberBand(dx, 120, 0.35);
            row.style.transform = 'translateX(' + damped + 'px)';
            return;
        }

        this._ensurePane(row, this.dir);
        row.style.transform = 'translateX(' + dx + 'px)';

        // LEFT (view): crossing the hook hands the gesture to the page-slide phase. The
        // hook distance is the width needed to show the icon + "View" stack (measured in
        // _ensurePane), falling back to a fraction of the row if measurement is missing.
        if (this.dir < 0) {
            var hookPx = this.viewHookPx || width * HOOK_FRACTION;
            if (Math.abs(dx) >= hookPx) this._enterHook(row, dx);
            return;
        }

        // RIGHT (action): arm/disarm the reveal+release fire.
        var armed = crossedThreshold(dx, MIN_FIRE_PX, FIRE_FRACTION, width);
        if (armed !== this.armed) {
            this.armed = armed;
            if (this.pane) this.pane.classList.toggle('is-armed', armed);
            if (armed) bump();                            // one pulse as it crosses to "fire"
        }
    }

    _up() {
        if (!this.active) return;

        // Hooked LEFT-swipe: the page-slide phase decides commit vs cancel.
        if (this.hooked) { this._releaseHook(); return; }

        var row = this.active;
        var axis = this.axis;
        var dir = this.dir;
        var fire = this.armed;
        this.active = null;
        this.axis = null;
        this.armed = false;

        if (axis !== 'h') { this._resetRow(row); this._removePane(); return; }

        // A horizontal drag must not also fire the card's tap-to-expand handler.
        this._swallowNextClick();

        if (fire && dir > 0) {
            // EXCEPTION: a data-confirm-email form defers its stream behind a modal that
            // can be cancelled. The action only fires after the user confirms, so there's
            // no continuous gesture to carry into the result. Snap home under the modal
            // fade-in; a cancelled confirm then leaves nothing stranded.
            if (this.primaryForm && this.primaryForm.hasAttribute('data-confirm-email')) {
                this._firePrimary();
                this._snapBack(row);
                this._removePane();
            } else {
                this._fireAction(row, dir);
            }
            return;
        }
        // An un-hooked LEFT swipe (or a sub-threshold RIGHT) just snaps back.
        this._snapBack(row);
        this._removePane();
    }

    // --- LEFT-swipe interactive page transition -----------------------------

    _enterHook(row, dx) {
        this.hooked = true;
        this.hookDx = dx;
        this.viewW = window.innerWidth || row.offsetWidth || 1;
        // Keep the View pane: it rides the page leftward behind the frozen card, so the
        // action background stays visible as the detail page slides in over it.
        if (this.pane) this.pane.classList.add('is-armed');
        // Freeze the card at the hook offset; from here it rides the receding page.
        row.style.transition = '';
        row.style.transform = 'translateX(' + dx + 'px)';
        bump('light');
        this._buildOverlay(this.viewUrl);
    }

    _trackPage(e) {
        if (!this.overlay) return;
        if (e.cancelable) e.preventDefault();        // own the gesture; no native scroll/zoom
        var dx = e.clientX - this.startX;            // negative, finger moving left
        // d = how far the finger has travelled PAST the hook point. Both layers move
        // by exactly this, 1:1 with the finger and with each other — no parallax, no
        // speed difference. Outgoing page slides left, incoming detail follows it in.
        var d = Math.abs(dx) - Math.abs(this.hookDx);
        d = Math.max(0, Math.min(this.viewW, d));
        this.progress = d / this.viewW;
        this.overlay.style.transform = 'translateX(' + (this.viewW - d) + 'px)';  // off-right -> 0
        document.body.style.transform = 'translateX(' + (-d) + 'px)';             // 0 -> off-left
    }

    _releaseHook() {
        var row = this.active;
        var commit = this.progress >= COMMIT_FRACTION;
        this.active = null;
        this.axis = null;
        this.hooked = false;
        this._swallowNextClick();
        if (commit) {
            this._commitView();
        } else {
            this._cancelHook(row);
        }
    }

    _commitView() {
        var self = this;
        var overlay = this.overlay;
        var url = (overlay && overlay.dataset.url) || this.viewUrl;
        this.committing = true;
        bump('light');
        // Settle the slide to its end state, then hand off to Turbo. The overlay stays
        // up (covering the body swap) until its own turbo:load handler removes it.
        if (overlay) {
            overlay.style.transition = 'transform 0.18s ease-out';
            overlay.style.transform = 'translateX(0)';
        }
        document.body.style.transition = 'transform 0.18s ease-out';
        document.body.style.transform = 'translateX(' + (-this.viewW) + 'px)';   // fully off-left, matching the incoming page

        var cleanup = function () {
            if (overlay && overlay.parentNode) overlay.remove();
            self._resetBody();
        };
        // document persists across the Turbo swap, so this fires after the real page lands.
        document.addEventListener('turbo:load', cleanup, { once: true });
        // Safety net: if Turbo never navigates (no Turbo, blocked visit), don't strand the overlay.
        setTimeout(function () { if (self.committing) { cleanup(); self.committing = false; } }, 4000);

        if (url && window.Turbo && typeof window.Turbo.visit === 'function') {
            // This overlay owns the visuals of the visit it triggers. The one
            // gesture handshake: turbo-navigation.js stamps the visit kind none (no
            // View Transition) and consumes the flag; skeleton-nav.js stands down
            // (no skeleton snapshot) while it is present.
            document.documentElement.setAttribute('data-nav-gesture', '');
            window.Turbo.visit(url);
        } else if (url) {
            window.location.href = url;
        }
        this.overlay = null;
        this.overlayPage = null;
    }

    _cancelHook(row) {
        var self = this;
        if (this._swipeFetchController) { this._swipeFetchController.abort(); this._swipeFetchController = null; }
        var overlay = this.overlay;
        if (overlay) {
            overlay.style.transition = 'transform 0.2s ease-out';
            overlay.style.transform = 'translateX(100%)';
        }
        document.body.style.transition = 'transform 0.2s ease-out';
        document.body.style.transform = '';
        setTimeout(function () {
            if (overlay && overlay.parentNode) overlay.remove();
            self._resetBody();
            self.overlay = null;
            self.overlayPage = null;
            self._removePane();           // drop the View background once the card is home
        }, 220);
        if (row) this._snapBack(row);
    }

    // Build the incoming detail layer: the shared detail skeleton instantly, with the
    // real fetched content swapped in when it arrives. Lives on <html> so it is NOT
    // moved by the outgoing page's body transform and survives the Turbo <body> swap.
    _buildOverlay(url) {
        var overlay = document.createElement('div');
        overlay.className = 'cs-view-overlay';
        overlay.dataset.url = url || '';
        overlay.style.transform = 'translateX(100%)';
        var page = document.createElement('div');
        page.className = 'cs-view-overlay__page';
        overlay.appendChild(page);
        document.documentElement.appendChild(overlay);
        this.overlay = overlay;
        this.overlayPage = page;
        // One shared builder owns the skeleton: same clone + same prefill echo the
        // normal nav uses. No per-caller wiring — identical behaviour by construction.
        if (window.ChippieSkeleton && window.ChippieSkeleton.buildDetailSkeleton) {
            window.ChippieSkeleton.buildDetailSkeleton(url, page);
        }
        if (url) this._fetchReal(url);
    }

    _fetchReal(url) {
        var self = this;
        var overlay = this.overlay;
        var controller = new AbortController();
        this._swipeFetchController = controller;
        fetch(url, { headers: { 'X-Requested-With': 'fetch' }, credentials: 'same-origin', signal: controller.signal })
            .then(function (r) { return r.ok ? r.text() : null; })
            .then(function (html) {
                if (controller.signal.aborted) return;
                self._swipeFetchController = null;
                // Bail if the gesture was cancelled (overlay swapped/removed) meanwhile.
                if (!html || !self.overlayPage || self.overlay !== overlay) return;
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var realContainer = doc.querySelector('.container.mt-4');
                if (!realContainer) return;
                var zone = doc.querySelector('#page-header-zone');
                var page = self.overlayPage;
                page.innerHTML = '';
                if (zone) { while (zone.firstChild) page.appendChild(zone.firstChild); }
                var c = document.createElement('div');
                c.className = 'container mt-4';
                while (realContainer.firstChild) c.appendChild(realContainer.firstChild);
                page.appendChild(c);
            })
            .catch(function () { /* keep the skeleton; Turbo.visit loads the real page on commit */ });
    }

    _teardownOverlay() {
        if (this.overlay && this.overlay.parentNode) this.overlay.remove();
        this.overlay = null;
        this.overlayPage = null;
        this._resetBody();
    }

    // Clear any page-slide transform/transition left on the body.
    _resetBody() {
        document.body.style.transition = '';
        document.body.style.transform = '';
    }

    // --- firing -------------------------------------------------------------

    _firePrimary() {
        // Submit the existing tagged form: reuses CSRF, the data-confirm-email modal,
        // and the Turbo Stream that replaces this row with its new status.
        var form = this.primaryForm;
        if (!form) return;
        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
        } else {
            form.submit();
        }
    }

    // --- pane (the revealed action behind the card) -------------------------

    _ensurePane(row, dir) {
        if (this.pane && this.pane.dataset.dir === String(dir)) {
            return;   // already revealed for this direction; box is fixed for the drag
        }
        this._removePane();

        var form = dir > 0 ? this.primaryForm : null;
        var label = dir > 0 ? (form.dataset.cardActionLabel || 'Action') : 'View';
        var icon = dir > 0 ? (form.dataset.cardActionIcon || 'bi-check-lg') : 'bi-eye';
        var kind = dir > 0 ? (form.dataset.cardActionKind || 'primary') : 'view';

        var pane = document.createElement('div');
        // Right-swipe reveals the pane on the LEFT edge; left-swipe on the RIGHT edge.
        pane.className = 'card-swipe-pane card-swipe-pane--' + kind + (dir > 0 ? ' is-left' : ' is-right');
        pane.dataset.dir = String(dir);
        pane.innerHTML = '<span class="card-swipe-pane-inner"><i class="bi ' + icon +
            '"></i><span class="card-swipe-pane-label">' + label + '</span></span>';
        this.element.appendChild(pane);
        this.pane = pane;
        row.style.zIndex = '1';
        // Pin the pane to the row's captured (untransformed) box so it stays put while
        // the card slides over it.
        var b = this.baseBox;
        if (b) {
            pane.style.top = b.top + 'px';
            pane.style.left = b.left + 'px';
            pane.style.width = b.width + 'px';
            pane.style.height = b.height + 'px';
        }

        // For the view (left-swipe), the hook should fire as soon as the card has slid
        // far enough to comfortably show the stacked icon + "View" label — not a flat
        // fraction of the row. Measure the revealed content and add the pane's own
        // horizontal padding on each side as breathing room.
        if (dir < 0) {
            var inner = pane.querySelector('.card-swipe-pane-inner');
            var padX = parseFloat(getComputedStyle(pane).paddingLeft) || 20;
            this.viewHookPx = (inner ? inner.offsetWidth : 0) + padX * 2;
        }
    }

    _removePane() {
        if (this.pane) { this.pane.remove(); this.pane = null; }
    }

    // Fire the action and own the WHOLE completion motion — no hold, no fade, no
    // handoff to turbo-animations.js. The card continues the gesture and flings the
    // rest of the way off-screen the instant you release. When the row's stream lands
    // we continue from there based on what the server did:
    //   - remove (row gone): the flung card is removed once it's fully off-screen and
    //     the rows below slide up to close the gap. One continuous slide-out.
    //   - replace (row stays, new status): the updated row slides back in from the
    //     same edge the old card left by, carrying the new status in.
    // While we own a row, turbo-animations.js stands down for it (the shared claim set),
    // so the two systems never animate the same element.
    _fireAction(row, dir) {
        var self = this;
        var targetId = row.id;
        if (!targetId) { this._firePrimary(); this._snapBack(row); this._removePane(); return; }

        var offscreen = dir * (window.innerWidth || row.offsetWidth || 600) * 1.1;
        var flingDone = false;
        var removePending = false;

        row.classList.remove('is-swiping');
        this._claim(targetId);
        bump('medium');
        this._firePrimary();

        // Fling: continue straight off-screen from wherever the finger let go.
        row.style.transition = 'transform ' + ACTION_FLING_MS + 'ms ' + motionToken('--ease-settle');
        row.style.transform = 'translateX(' + offscreen + 'px)';

        var removed = false;
        var doRemove = function () {
            if (removed) return;               // transitionend + fallback must not double-fire
            removed = true;
            self._endFire(targetId);
            self._completeRemove(targetId);
        };
        row.addEventListener('transitionend', function te(ev) {
            if (ev.propertyName !== 'transform') return;
            row.removeEventListener('transitionend', te);
            flingDone = true;
            if (removePending) doRemove();
        });

        var timer;
        var onStream = function (event) {
            var s = event.detail.newStream;
            if (!s || s.target !== targetId) return;   // ignore metrics / other rows
            document.removeEventListener('turbo:before-stream-render', onStream);
            clearTimeout(timer);

            if (s.action === 'remove') {
                event.preventDefault();                 // we own the exit
                if (flingDone) doRemove();
                else {
                    removePending = true;               // close the gap once the card is fully off-screen
                    setTimeout(doRemove, ACTION_FLING_MS + 80);   // fallback if transitionend is missed
                }
            } else if (s.action === 'replace') {
                var original = event.detail.render;
                event.detail.render = function (el) {
                    return Promise.resolve(original(el)).then(function () {
                        var node = document.getElementById(targetId);
                        if (node) self._slideRowIn(node, dir);   // set start transform pre-paint
                        self._endFire(targetId);                  // unclaim after the new row is positioned
                        self._removePane();
                    });
                };
            } else {
                self._endFire(targetId);
                self._removePane();
            }
        };
        document.addEventListener('turbo:before-stream-render', onStream);

        // Leak guard: no stream (server error / no Turbo) -> bring the card back.
        timer = setTimeout(function () {
            document.removeEventListener('turbo:before-stream-render', onStream);
            self._fireTimer = null;
            self._endFire(targetId);
            self._snapBack(row);
            self._removePane();
        }, 4000);
        this._fireTimer = timer;

        this._firingId = targetId;
        this._streamHandler = onStream;
    }

    // Remove: the flung card is gone off-screen; drop it and slide the rows below up
    // to close the gap (FLIP). The card never fades or stops mid-way.
    _completeRemove(targetId) {
        var element = document.getElementById(targetId);
        this._removePane();
        if (!element) return;

        var following = [];
        var sib = element.nextElementSibling;
        while (sib) { following.push({ el: sib, top: sib.getBoundingClientRect().top }); sib = sib.nextElementSibling; }

        element.remove();   // gap collapses; rows below jump up to their new positions

        // Per-row measurement: each follower moved by its own amount, unlike the
        // notification list where every row shifts by one uniform height.
        flipSettle(
            following.map(function (f) {
                return { el: f.el, delta: f.top - f.el.getBoundingClientRect().top };
            }),
            ACTION_SETTLE_MS,
            'ease-out'   // preserved verbatim; easing convergence is chippie-xf13v.1
        );
    }

    // Replace: the updated row slides in from the same edge the old card flung off by.
    _slideRowIn(node, dir) {
        var offscreen = dir * (window.innerWidth || node.offsetWidth || 600) * 1.1;
        node.style.transition = 'none';
        node.style.transform = 'translateX(' + offscreen + 'px)';
        void node.offsetWidth;             // commit the off-screen start before transitioning
        requestAnimationFrame(function () {
            node.style.transition = 'transform ' + ACTION_SETTLE_MS + 'ms ' + motionToken('--ease-settle');
            node.style.transform = 'translateX(0)';
            setTimeout(function () { node.style.transition = ''; node.style.transform = ''; }, ACTION_SETTLE_MS);
        });
    }

    // Shared claim set: tells turbo-animations.js to stand down for a row while this
    // controller is carrying it through the swipe completion.
    _claim(id) {
        if (!window.__cardSwipeFiring) window.__cardSwipeFiring = {};
        window.__cardSwipeFiring[id] = true;
    }

    _endFire(id) {
        if (window.__cardSwipeFiring) delete window.__cardSwipeFiring[id];
        this._firingId = null;
        this._streamHandler = null;
    }

    // --- helpers ------------------------------------------------------------

    _primaryForm(row) {
        return row.querySelector('form[data-card-primary-action]');
    }

    _viewUrl(row) {
        var eye = row.querySelector('.mobile-card-eye[href], a.btn-view[href]');
        return eye ? eye.getAttribute('href') : null;
    }

    _snapBack(row) {
        row.classList.remove('is-swiping');
        row.style.transition = 'transform 0.2s ' + motionToken('--ease-settle');
        row.style.transform = '';
        var self = this;
        setTimeout(function () { self._resetRow(row); }, 220);
    }

    _resetRow(row) {
        row.classList.remove('is-swiping');
        row.style.transition = '';
        row.style.transform = '';
        row.style.zIndex = '';
    }

    _swallowNextClick() {
        var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
        document.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(function () {
            document.removeEventListener('click', swallow, { capture: true });
        }, 400);
    }

}
